import { inject, Injectable } from '@angular/core';
import { API_URLS, WebServiceProvider } from '@pinnakl/core/web-services';
import { SecurityMarket, SecurityMarketDetails } from '@pinnakl/securities/domain';
import { getErrorMessage } from '@pinnakl/shared/util-helpers';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';

export type SecurityMarketsFilter = {
  securityId: number | null;
};

@Injectable({
  providedIn: 'root',
})
export class SecurityMarketsApiService {
  private readonly wsp = inject(WebServiceProvider);
  private readonly apiUrls = inject(API_URLS);
  private readonly toast = inject(PinnaklUIToastMessage);

  async loadSecurityMarkets(filter: SecurityMarketsFilter): Promise<SecurityMarket[]> {
    try {
      return await this.getSecurityMarkets(filter);
    } catch (error) {
      this.toast.warning('Unable to load security markets.' + getErrorMessage('', error));
      throw error;
    }
  }

  async loadSecurityMarketDetails(securityMarketId: number): Promise<SecurityMarketDetails> {
    try {
      return await this.wsp.get<SecurityMarketDetails>({
        endpoint: this.apiUrls.securityMarketDetails,
        params: { securityMarketId },
      });
    } catch (error) {
      this.toast.warning('Unable to load security market details.' + getErrorMessage('', error));
      throw error;
    }
  }

  async loadAllSecurityMarketDetails(securityMarketIds: number[]): Promise<SecurityMarketDetails[]> {
    try {
      return await this.wsp.get<SecurityMarketDetails[]>({
        endpoint: this.apiUrls.securityMarketBatchDetails,
        queryParams: { securityMarketIds: securityMarketIds.join(',') },
      });
    } catch (error) {
      this.toast.warning('Unable to load security market details.' + getErrorMessage('', error));
      throw error;
    }
  }

  private getSecurityMarkets(filter: SecurityMarketsFilter): Promise<SecurityMarket[]> {
    return this.wsp.get<SecurityMarket[]>({
      endpoint: this.apiUrls.securityMarketsForSecurity,
      params: { securityId: filter.securityId ?? '' },
    });
  }
}
